import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';

const CSS = `
  @keyframes ob-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
  @keyframes ob-in    { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:none} }
  @keyframes ob-glow  { 0%,100%{box-shadow:0 0 20px rgba(232,0,111,.4)} 50%{box-shadow:0 0 40px rgba(232,0,111,.7)} }
  @keyframes ob-spin  { to{transform:rotate(360deg)} }
  @keyframes ob-pulse { 0%,100%{opacity:.7} 50%{opacity:1} }
  .ob-card { opacity:0; animation: ob-in .5s cubic-bezier(.16,1,.3,1) forwards; }
  .ob-card:nth-child(1){animation-delay:.05s}
  .ob-card:nth-child(2){animation-delay:.12s}
  .ob-card:nth-child(3){animation-delay:.19s}
  .ob-card:nth-child(4){animation-delay:.26s}
  .ob-float { animation: ob-float 3s ease-in-out infinite; }
  .ob-glow  { animation: ob-glow 2.5s ease-in-out infinite; }
`;

const STEPS = [
  {
    num: 1, emoji: '👤', title: 'Заполни профиль', done: null,
    desc: 'Укажи пол, дату рождения, рост, цель и уровень активности. Без этого не будет расчёта КБЖУ и % жира.',
    link: '/profile', btn: 'Заполнить профиль',
    color: '#E8006F', bg: 'rgba(232,0,111,.12)', border: 'rgba(232,0,111,.25)',
  },
  {
    num: 2, emoji: '📏', title: 'Добавь первый замер', done: null,
    desc: 'Сделай замеры утром, натощак, после туалета. Одна лента, одна одежда, одно время.',
    link: '/measurements/new', btn: 'Добавить замер',
    color: '#00CFDB', bg: 'rgba(0,207,219,.12)', border: 'rgba(0,207,219,.25)',
  },
  {
    num: 3, emoji: '📊', title: 'Смотри прогресс', done: null,
    desc: 'После второго замера появится динамика — зелёный прогресс, красный регресс. Раздел «Прогресс» покажет сравнительную таблицу.',
    link: '/progress', btn: 'Открыть прогресс',
    color: '#9B6FD4', bg: 'rgba(155,111,212,.12)', border: 'rgba(155,111,212,.25)',
  },
];

export default function Onboarding({ profileComplete, hasMeasurements }) {
  const { user } = useAuth();
  const steps = STEPS.map((s, i) => ({
    ...s,
    done: i === 0 ? profileComplete : i === 1 ? hasMeasurements : hasMeasurements,
  }));

  return (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      <style>{CSS}</style>

      {/* Hero */}
      <div className="ob-card" style={{
        position: 'relative', overflow: 'hidden', borderRadius: 20, padding: '1.75rem',
        background: 'linear-gradient(135deg,rgba(232,0,111,.15) 0%,rgba(155,111,212,.1) 60%,rgba(0,207,219,.08) 100%)',
        border: '1.5px solid rgba(232,0,111,.3)', marginBottom: '1rem',
        boxShadow: '0 16px 40px rgba(232,0,111,.12)',
      }}>
        {/* BG orb */}
        <div style={{ position:'absolute', top:-30, right:-30, width:180, height:180, borderRadius:'50%',
                      background:'radial-gradient(circle,rgba(232,0,111,.2) 0%,transparent 70%)', filter:'blur(20px)' }}/>

        <div style={{ display:'flex', alignItems:'center', gap:'1rem', position:'relative', zIndex:1 }}>
          <div className="ob-float ob-glow" style={{
            width:56, height:56, borderRadius:16, flexShrink:0,
            background:'linear-gradient(135deg,#E8006F,#9B6FD4)',
            display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.5rem',
          }}>
            {user?.first_name?.[0] || '👋'}
          </div>
          <div>
            <h2 style={{ color:'#F2EEF9', fontFamily:'system-ui', fontWeight:800, fontSize:'1.3rem', margin:'0 0 4px', letterSpacing:-.5 }}>
              Добро пожаловать{user?.first_name ? `, ${user.first_name}` : ''}! 🎉
            </h2>
            <p style={{ color:'rgba(158,140,184,.9)', fontSize:'.85rem', margin:0 }}>
              Давай настроим всё за 3 шага
            </p>
          </div>
        </div>

        <p style={{ color:'rgba(242,238,249,.75)', fontSize:'.85rem', lineHeight:1.6, marginTop:'1rem', marginBottom:0, position:'relative', zIndex:1 }}>
          MolShape автоматически считает КБЖУ, % жира, ИМТ и соотношения тела.
          Тренер Алёна видит твои результаты и оставляет рекомендации.
        </p>
      </div>

      {/* Steps */}
      {steps.map((s, i) => (
        <div key={i} className="ob-card" style={{
          borderRadius: 16, padding: '1.25rem', marginBottom: '.75rem',
          background: s.done ? 'rgba(26,21,37,.5)' : `rgba(26,21,37,.95)`,
          border: `1.5px solid ${s.done ? 'rgba(34,197,94,.2)' : s.border}`,
          opacity: s.done ? 1 : 1,
          transition: 'all .2s',
        }}>
          <div style={{ display:'flex', alignItems:'flex-start', gap:'1rem' }}>
            {/* Icon circle */}
            <div style={{
              width:44, height:44, borderRadius:12, flexShrink:0,
              background: s.done ? 'rgba(34,197,94,.15)' : s.bg,
              border: `1.5px solid ${s.done ? 'rgba(34,197,94,.3)' : s.border}`,
              display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.2rem',
            }}>
              {s.done ? '✅' : s.emoji}
            </div>

            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                <span style={{ color:'rgba(158,140,184,.5)', fontSize:'.7rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em' }}>
                  Шаг {s.num}
                </span>
                {s.done && <span style={{ color:'#22C55E', fontSize:'.7rem', fontWeight:700 }}>✓ Готово</span>}
              </div>
              <h3 style={{ color: s.done ? 'rgba(242,238,249,.5)' : '#F2EEF9', fontFamily:'system-ui', fontWeight:700, fontSize:'1rem', margin:'0 0 6px' }}>
                {s.title}
              </h3>
              <p style={{ color:'rgba(158,140,184,.7)', fontSize:'.8rem', lineHeight:1.55, margin:'0 0 .75rem' }}>
                {s.desc}
              </p>
              {!s.done && (
                <Link to={s.link} style={{
                  display:'inline-flex', alignItems:'center', gap:6,
                  padding:'.5rem 1.1rem', borderRadius:10,
                  background: s.color, color:'#fff', textDecoration:'none',
                  fontSize:'.8rem', fontWeight:700,
                  boxShadow:`0 4px 16px ${s.color}55`,
                }}>
                  {s.btn} →
                </Link>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Protocol hint */}
      <div className="ob-card" style={{
        borderRadius:14, padding:'1rem 1.25rem', marginTop:'.25rem',
        background:'rgba(255,255,255,.03)', border:'1px solid rgba(255,255,255,.07)',
      }}>
        <p style={{ color:'rgba(158,140,184,.7)', fontSize:'.78rem', fontWeight:700, margin:'0 0 8px' }}>
          📏 Протокол замеров от тренера:
        </p>
        {['Утром, натощак, после туалета','Одна лента, одна одежда','Желательно в одно и то же время','Замеры раз в 1-2 недели'].map((t,i) => (
          <p key={i} style={{ color:'rgba(158,140,184,.55)', fontSize:'.78rem', margin:'0 0 3px', display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ color:'rgba(232,0,111,.6)', fontWeight:700 }}>·</span> {t}
          </p>
        ))}
      </div>
    </div>
  );
}
